import { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useNetworkStore } from '../store/networkStore';
import { useGameStore } from '../store/gameStore';
import { useSettingsStore } from '../store/settingsStore';
import GameRenderer from '../game/GameRenderer';
import GameHUD from '../components/GameHUD';
import Countdown from '../components/Countdown';
import GameOverlay from '../components/GameOverlay';
import './Game.css';

function Game() {
  const navigate = useNavigate();
  const { roomId } = useParams();
  const containerRef = useRef<HTMLDivElement>(null);
  
  const { room, localPlayerId } = useNetworkStore();
  const { countdown, raceTimer, respawning } = useGameStore();
  const { showMinimap, soundEnabled, musicEnabled } = useSettingsStore();
  
  const [isPaused, setIsPaused] = useState(false);

  // Navigate to results when race ends
  useEffect(() => {
    if (room?.state === 'results') {
      navigate(`/results/${room.id}`);
    }
  }, [room?.state, room?.id, navigate]);

  // Redirect if no room
  useEffect(() => {
    if (!room) {
      navigate('/lobby');
    }
  }, [room, navigate]);

  // Handle escape key for pause
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setIsPaused(prev => !prev);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  if (!room) {
    return null;
  }

  const localPlayer = room.players.find(p => p.id === localPlayerId);

  return (
    <div className="screen game-screen">
      <div className="game-container" ref={containerRef}>
        <GameRenderer 
          containerRef={containerRef}
          room={room}
          localPlayerId={localPlayerId}
        />
      </div>

      {/* HUD Overlay */}
      <GameHUD 
        room={room}
        localPlayerId={localPlayerId}
        raceTimer={raceTimer}
        showMinimap={showMinimap}
      />

      {/* Countdown Overlay */}
      {room.state === 'countdown' && countdown !== null && (
        <Countdown value={countdown} />
      )}

      {/* Respawning Overlay */}
      {respawning && (
        <GameOverlay 
          text="Respawning"
          color="#f59e0b"
        />
      )}

      {/* Pause Menu */}
      {isPaused && (
        <div className="pause-overlay">
          <div className="pause-menu card">
            <h2>Paused</h2>
            <div className="pause-options">
              <button 
                className="btn btn-primary" 
                onClick={() => setIsPaused(false)}
              >
                Resume
              </button>
              <button 
                className="btn btn-secondary"
                onClick={() => {
                  // Toggle settings
                }}
              >
                Settings
              </button>
              <button 
                className="btn btn-ghost"
                onClick={() => {
                  const { leaveRoom } = useNetworkStore.getState();
                  leaveRoom();
                  navigate('/lobby');
                }}
              >
                Leave Race
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Game;
